import { useNavigate } from "react-router-dom";
import { useSkills, useCreateSkillsThread } from "../../hooks/useSkills";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";

interface SkillsListProps {
  brandId: string;
}

export function SkillsList({ brandId }: SkillsListProps) {
  const { data: skillsData, isLoading } = useSkills(brandId);
  const createThread = useCreateSkillsThread();
  const navigate = useNavigate();

  const skills = skillsData?.data ?? [];

  const handleCustomize = async () => {
    try {
      const result = await createThread.mutateAsync({ brand_id: brandId });
      navigate(`/skills/${result.data.id}`);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {skills.length} skill{skills.length !== 1 ? "s" : ""} loaded
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={handleCustomize}
          loading={createThread.isPending}
        >
          Customize with AI
        </Button>
      </div>

      <div className="space-y-2">
        {skills.map((skill) => (
          <div
            key={skill.slug}
            className="rounded-lg border border-slate-200 bg-white p-3"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-900">
                  {skill.name}
                </h3>
                <p className="mt-0.5 text-xs text-slate-400">
                  {skill.slug} &middot; Phase {skill.phase} &middot; v{skill.version}
                </p>
              </div>
              <Badge variant={skill.source === "brand" ? "success" : "default"}>
                {skill.source === "brand" ? "Customized" : "Default"}
              </Badge>
            </div>
          </div>
        ))}
      </div>

      {skills.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">
          No skills loaded. Check your backend connection.
        </p>
      )}
    </div>
  );
}
